import * as tslib_1 from "tslib";
import React from 'react';
import keycode from 'keycode';
import hasIn from 'lodash/hasIn';
import assign from 'lodash/assign';
import TouchRipple from '../TouchRipple';
import createRippleHandler from './createRippleHandler';
import { listenForFocusKeys, detectKeyboardFocus, focusKeyPressed, } from '../utils/keyboardFocus';
/**
 * 给包装的组件添加点击Ripple效果
 *
 * @param {React.ComponentType<{}> | string} Component 被包装的组件
 */
export default function withRipple(Component) {
    var _a;
    var componentName = typeof Component === 'string'
        ? Component
        : Component.displayName || Component.name || 'Component';
    return _a = /** @class */ (function (_super) {
            tslib_1.__extends(RippleableComponent, _super);
            function RippleableComponent(props) {
                var _this = _super.call(this, props) || this;
                _this.onKeyboardFocusHandler = function (event) {
                    _this.keyDown = false;
                    _this.setState({ keyboardFocused: true });
                    if (_this.props.onKeyboardFocus) {
                        _this.props.onKeyboardFocus(event);
                    }
                };
                _this.innerDom = null;
                _this.keyDown = false; // Used to help track keyboard activation keyDown
                _this.keyboardFocusTimeout = null;
                _this.keyboardFocusCheckTime = 50;
                _this.keyboardFocusMaxCheckTimes = 5;
                _this.handleKeyDown = function (event) {
                    var _a = _this.props, focusRipple = _a.focusRipple, onKeyDown = _a.onKeyDown, onClick = _a.onClick;
                    var key = keycode(event.nativeEvent);
                    if (focusRipple &&
                        !_this.keyDown &&
                        _this.state.keyboardFocused &&
                        key === 'space') {
                        _this.keyDown = true;
                        event.persist();
                        if (_this.ripple) {
                            _this.ripple.stop(event, function () {
                                if (_this.ripple) {
                                    _this.ripple.start(event);
                                }
                            });
                        }
                    }
                    if (onKeyDown) {
                        onKeyDown(event);
                    }
                    var isInteractiveElement = Component === 'button' || Component === 'a';
                    var enableKeyboardClick = hasIn(_this.props, 'disableKeyboardClick')
                        ? !_this.props.disableKeyboardClick
                        : isInteractiveElement;
                    // Keyboard accessibility for non interactive elements
                    if (event.target === _this.innerDom &&
                        onClick &&
                        enableKeyboardClick &&
                        (key === 'space' || key === 'enter')) {
                        event.preventDefault();
                        onClick(event);
                    }
                };
                _this.handleKeyUp = function (event) {
                    if (_this.props.focusRipple &&
                        keycode(event.nativeEvent) === 'space' &&
                        _this.state.keyboardFocused) {
                        _this.keyDown = false;
                        event.persist();
                        if (_this.ripple) {
                            _this.ripple.stop(event, function () { return _this.ripple && _this.ripple.pulsate(); });
                        }
                    }
                    if (_this.props.onKeyUp) {
                        _this.props.onKeyUp(event);
                    }
                };
                _this.handleMouseDown = createRippleHandler(_this, 'MouseDown', 'start', function () {
                    clearTimeout(_this.keyboardFocusTimeout);
                    focusKeyPressed(false);
                    if (_this.state.keyboardFocused) {
                        _this.setState({ keyboardFocused: false });
                    }
                });
                _this.handleMouseUp = createRippleHandler(_this, 'MouseUp', 'stop', function (event) {
                    if (_this.state.keyboardFocused) {
                        event.preventDefault();
                    }
                });
                _this.handleMouseLeave = createRippleHandler(_this, 'MouseLeave', 'stop', function (event) {
                    if (_this.state.keyboardFocused) {
                        event.preventDefault();
                    }
                });
                _this.handleTouchStart = createRippleHandler(_this, 'TouchStart', 'start', function () {
                    clearTimeout(_this.keyboardFocusTimeout);
                    focusKeyPressed(false);
                    if (_this.state.keyboardFocused) {
                        _this.setState({ keyboardFocused: false });
                    }
                });
                _this.handleTouchEnd = createRippleHandler(_this, 'TouchEnd', 'stop', function (event) {
                    if (_this.state.keyboardFocused) {
                        event.preventDefault();
                    }
                });
                _this.handleTouchMove = createRippleHandler(_this, 'TouchMove', 'stop', function (event) {
                    if (_this.state.keyboardFocused) {
                        event.preventDefault();
                    }
                });
                _this.handleBlur = createRippleHandler(_this, 'Blur', 'stop', function () {
                    clearTimeout(_this.keyboardFocusTimeout);
                    focusKeyPressed(false);
                    _this.setState({ keyboardFocused: false });
                });
                _this.handleFocus = function (event) {
                    if (_this.props.disabled) {
                        return;
                    }
                    if (!_this.innerDom) {
                        _this.innerDom = event.currentTarget;
                    }
                    event.persist();
                    if (_this.innerDom) {
                        detectKeyboardFocus(_this, _this.innerDom, function () {
                            _this.onKeyboardFocusHandler(event);
                        });
                    }
                    if (_this.props.onFocus) {
                        _this.props.onFocus(event);
                    }
                };
                _this.state = {
                    keyboardFocused: false,
                };
                return _this;
            }
            RippleableComponent.prototype.componentDidMount = function () {
                if (this.props.withRippleRef) {
                    this.props.withRippleRef(this);
                }
                listenForFocusKeys();
            };
            RippleableComponent.prototype.componentWillReceiveProps = function (nextProps) {
                if (!this.props.disabled &&
                    nextProps.disabled &&
                    this.state.keyboardFocused) {
                    this.setState({
                        keyboardFocused: false,
                    });
                }
            };
            RippleableComponent.prototype.componentWillUpdate = function (nextProps, nextState) {
                if (this.ripple &&
                    this.props.focusRipple &&
                    nextState.keyboardFocused &&
                    !this.state.keyboardFocused &&
                    !this.props.disableRipple) {
                    this.ripple.pulsate();
                }
            };
            RippleableComponent.prototype.componentWillUnmount = function () {
                if (this.props.withRippleRef) {
                    this.props.withRippleRef(null);
                }
                this.innerDom = null;
                clearTimeout(this.keyboardFocusTimeout);
            };
            RippleableComponent.prototype.render = function () {
                var _this = this;
                var _a = this.props, centerRipple = _a.centerRipple, children = _a.children, disabled = _a.disabled, disableRipple = _a.disableRipple, focusRipple = _a.focusRipple, onBlur = _a.onBlur, onFocus = _a.onFocus, onKeyboardFocus = _a.onKeyboardFocus, onKeyDown = _a.onKeyDown, onKeyUp = _a.onKeyUp, onMouseDown = _a.onMouseDown, onMouseLeave = _a.onMouseLeave, onMouseUp = _a.onMouseUp, onTouchEnd = _a.onTouchEnd, onTouchMove = _a.onTouchMove, onTouchStart = _a.onTouchStart, innerRef = _a.innerRef, tabIndex = _a.tabIndex, style = _a.style, focusedStyle = _a.focusedStyle, theme = _a.theme, disableKeyboardClick = _a.disableKeyboardClick, rippleRef = _a.rippleRef, withRippleRef = _a.withRippleRef, rest = tslib_1.__rest(_a, ["centerRipple", "children", "disabled", "disableRipple", "focusRipple", "onBlur", "onFocus", "onKeyboardFocus", "onKeyDown", "onKeyUp", "onMouseDown", "onMouseLeave", "onMouseUp", "onTouchEnd", "onTouchMove", "onTouchStart", "innerRef", "tabIndex", "style", "focusedStyle", "theme", "disableKeyboardClick", "rippleRef", "withRippleRef"]);
                var wrapperStyle = this.state.keyboardFocused
                    ? assign(style, focusedStyle || {})
                    : style;
                return (React.createElement(Component, tslib_1.__assign({ onBlur: this.handleBlur, onFocus: this.handleFocus, onKeyDown: this.handleKeyDown, onKeyUp: this.handleKeyUp, onMouseDown: this.handleMouseDown, onMouseLeave: this.handleMouseLeave, onMouseUp: this.handleMouseUp, onTouchEnd: this.handleTouchEnd, onTouchMove: this.handleTouchMove, onTouchStart: this.handleTouchStart, tabIndex: disabled ? -1 : tabIndex, ref: innerRef, disabled: disabled }, rest, { style: wrapperStyle }),
                    children,
                    !disableRipple && !disabled ? (React.createElement(TouchRipple, { ref: function (node) {
                            _this.ripple = node;
                            if (rippleRef) {
                                rippleRef(node);
                            }
                        }, center: centerRipple })) : null));
            };
            return RippleableComponent;
        }(React.Component)),
        _a.displayName = "WithRipple(" + componentName + ")",
        _a;
}
//# sourceMappingURL=withRipple.js.map